import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import useAsyncDispatch from 'hooks/useAsyncDispatch';
import { fetchDevice } from 'modules/devices/actions/core';
import { getDeviceById } from 'modules/devices/selectors';
import { getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import type { RootState } from 'reducers/index';
import Ellipsis from 'ui/base/Ellipsis';
import Tooltip from 'ui/base/Tooltip';
import { b } from 'utils/i18nUtils';

import type { MediaGalleryActions, MediaListItem } from '../../types';
import Actions from '../Actions';
import { ItemWrapper, MediaActionsFooter } from '../UI';

type PendingMediaProps = {
  deviceId?: string;
  media: MediaListItem;
} & MediaGalleryActions;

const PendingMedia: React.FC<PendingMediaProps> = ({ deviceId, media, ...actions }) => {
  const dispatch = useAsyncDispatch();
  const [hasError, setHasError] = React.useState(false);

  const device = useSelector((state: RootState) => (deviceId ? getDeviceById(state, deviceId) : undefined));
  const readonly = useSelector(getCurrentUserIsReadOnly);

  const handleOnOpenChange = React.useCallback(async () => {
    if (!hasError && !device && deviceId) {
      try {
        await dispatch(fetchDevice(deviceId));
      } catch {
        setHasError(true);
      }
    }
  }, [dispatch, hasError, deviceId, device]);

  const deviceName = React.useMemo(() => {
    if (deviceId && !hasError) {
      return device?.name ?? <span className="fb-icon--spin fb-icon-spin" />;
    }
    return <FormattedMessage id="unknown" defaultMessage="Unknown" />;
  }, [deviceId, hasError, device?.name]);

  return (
    <React.Fragment>
      <ItemWrapper>
        <i className="fb-icon--media-loading" />
      </ItemWrapper>

      <MediaActionsFooter className="text-center">
        <div className="text-center w-100 d-flex gap-2 align-items-center justify-content-center pl-5 pr-5">
          <Ellipsis>
            <FormattedMessage id="page.fiche.form.pending.title" defaultMessage="Pending" />
          </Ellipsis>
          <Tooltip
            placement="top"
            onOpenChange={handleOnOpenChange}
            styles={{ container: { textAlign: 'center' } }}
            title={(
              <span className="text-center">
                <FormattedMessage
                  id="page.fiche.medias.pending"
                  defaultMessage={
                    'Media is being uploaded from device <b>"{deviceName}"</b>. ' +
                    'Please open Fleetback application on this device to have media uploaded.'
                  }
                  values={{ b, deviceName }}
                />
              </span>
            )}
          >
            <i className="fb-icon--info text-secondary cursor-help" />
          </Tooltip>
        </div>
        {!readonly && Boolean(actions.onDelete) && (
          <div className="position-absolute r-1">
            <Actions media={media} {...actions} />
          </div>
        )}
      </MediaActionsFooter>
    </React.Fragment>
  );
};

export default PendingMedia;
